import { createTheme, responsiveFontSizes } from "@mui/material/styles";

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - Variables reutilizables en todo el proyecto
// ═══════════════════════════════════════════════════════════════════════════

export const designTokens = {
  // Colores principales
  colors: {
    primary: "#00C9FF",
    primaryLight: "#33D4FF",
    primaryDark: "#0098CC",
    secondary: "#92FE9D",
    secondaryLight: "#A8FEAD",
    secondaryDark: "#6FCA7D",
    error: "#FF5252",
    warning: "#FFC107",
    success: "#4CAF50",
    info: "#00C9FF",
  },

  // Backgrounds
  backgrounds: {
    body: "#0a0e27",
    paper: "#131829",
    elevated: "#1a1f36",
    surface: "rgba(19, 24, 41, 0.95)",
    surfaceLight: "rgba(30, 37, 60, 0.95)",
  },

  // Gradientes reutilizables
  gradients: {
    primary: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)",
    primaryHover: "linear-gradient(135deg, #00B8E6 0%, #7FE08A 100%)",
    primaryVertical: "linear-gradient(180deg, #00C9FF 0%, #92FE9D 100%)",
    dark: "linear-gradient(135deg, #131829 0%, #1a1f36 100%)",
    glass:
      "linear-gradient(135deg, rgba(0,201,255,0.1) 0%, rgba(146,254,157,0.1) 100%)",
    success: "linear-gradient(135deg, #4CAF50 0%, #92FE9D 100%)",
    warning: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
    error: "linear-gradient(135deg, #FF5252 0%, #FF8A80 100%)",
  },

  // Bordes
  borders: {
    subtle: "rgba(255, 255, 255, 0.08)",
    light: "rgba(255, 255, 255, 0.1)",
    medium: "rgba(255, 255, 255, 0.15)",
    primary: "rgba(0, 201, 255, 0.25)",
    secondary: "rgba(146, 254, 157, 0.25)",
  },

  // Shadows
  shadows: {
    card: "0 4px 20px rgba(0, 0, 0, 0.25)",
    cardHover: "0 8px 30px rgba(0, 201, 255, 0.15)",
    elevated: "0 8px 32px rgba(0, 0, 0, 0.35)",
    glow: "0 0 20px rgba(0, 201, 255, 0.3)",
    glowSecondary: "0 0 20px rgba(146, 254, 157, 0.3)",
  },

  // Spacing consistente
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // Border radius
  radius: {
    sm: 6,
    md: 12,
    lg: 16,
    xl: 24,
    full: 9999,
  },

  // Transitions
  transitions: {
    fast: "0.15s ease",
    normal: "0.25s ease",
    slow: "0.35s ease",
    spring: "0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// MIXINS - Estilos reutilizables para sx props
// ═══════════════════════════════════════════════════════════════════════════

export const mixins = {
  // Card con efecto glass
  glassCard: {
    background: designTokens.backgrounds.surface,
    backdropFilter: "blur(10px)",
    border: `1px solid ${designTokens.borders.light}`,
    borderRadius: designTokens.radius.lg,
    boxShadow: designTokens.shadows.card,
    transition: designTokens.transitions.normal,
    "&:hover": {
      boxShadow: designTokens.shadows.cardHover,
      borderColor: designTokens.borders.primary,
    },
  },

  // Card KPI con gradiente sutil
  kpiCard: {
    background: designTokens.gradients.glass,
    backdropFilter: "blur(10px)",
    border: `1px solid ${designTokens.borders.primary}`,
    borderRadius: designTokens.radius.lg,
    boxShadow: designTokens.shadows.card,
    p: { xs: 2, md: 3 },
    transition: designTokens.transitions.normal,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: designTokens.shadows.cardHover,
    },
  },

  // Botón con gradiente
  gradientButton: {
    background: designTokens.gradients.primary,
    color: "#000",
    fontWeight: 700,
    "&:hover": {
      background: designTokens.gradients.primaryHover,
      transform: "translateY(-2px)",
      boxShadow: designTokens.shadows.glow,
    },
  },

  // Texto con gradiente
  gradientText: {
    background: designTokens.gradients.primary,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  // Contenedor responsivo
  responsiveContainer: {
    p: { xs: 2, sm: 3, md: 4 },
    maxWidth: "100%",
  },

  // Flex center
  flexCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // Flex between
  flexBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  // Stack vertical con gap
  stack: (gap = 2) => ({
    display: "flex",
    flexDirection: "column",
    gap,
  }),

  // Truncate text
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Hide en mobile
  hideOnMobile: {
    display: { xs: "none", sm: "block" },
  },

  // Show solo en mobile
  showOnMobile: {
    display: { xs: "block", sm: "none" },
  },

  // Tabla responsive
  responsiveTable: {
    overflowX: "auto",
    "& .MuiTable-root": {
      minWidth: { xs: 600, md: "100%" },
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// THEME CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

let theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: designTokens.colors.primary,
      light: designTokens.colors.primaryLight,
      dark: designTokens.colors.primaryDark,
      contrastText: "#000000",
    },
    secondary: {
      main: designTokens.colors.secondary,
      light: designTokens.colors.secondaryLight,
      dark: designTokens.colors.secondaryDark,
      contrastText: "#000000",
    },
    background: {
      default: designTokens.backgrounds.body,
      paper: designTokens.backgrounds.paper,
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0BEC5",
    },
    error: {
      main: designTokens.colors.error,
    },
    warning: {
      main: designTokens.colors.warning,
    },
    info: {
      main: designTokens.colors.info,
    },
    success: {
      main: designTokens.colors.success,
    },
  },
  // Breakpoints custom - Mobile First
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
      color: "#B0BEC5",
    },
  },
  shape: {
    borderRadius: designTokens.radius.md,
  },
  components: {
    // Botones mejorados
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: designTokens.radius.sm,
          padding: "10px 24px",
          transition: designTokens.transitions.normal,
          "@media (max-width: 600px)": {
            padding: "8px 16px",
            fontSize: "0.875rem",
          },
        },
        containedPrimary: {
          background: designTokens.gradients.primary,
          color: "#000",
          "&:hover": {
            background: designTokens.gradients.primaryHover,
            boxShadow: designTokens.shadows.glow,
          },
        },
      },
    },
    // Cards mejoradas
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          background: designTokens.backgrounds.paper,
          border: `1px solid ${designTokens.borders.light}`,
          borderRadius: designTokens.radius.lg,
          boxShadow: designTokens.shadows.card,
          transition: designTokens.transitions.normal,
          "&:hover": {
            borderColor: designTokens.borders.primary,
            boxShadow: designTokens.shadows.cardHover,
          },
        },
      },
    },
    // Paper mejorado
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: designTokens.radius.md,
        },
      },
    },
    // AppBar responsive
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backdropFilter: "blur(10px)",
        },
      },
    },
    // Drawer responsive
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${designTokens.borders.light}`,
          backgroundImage: "none",
        },
      },
    },
    // Tabla responsive
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.md,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            height: 8,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          borderBottom: `1px solid ${designTokens.borders.subtle}`,
          padding: "16px",
          "@media (max-width: 600px)": {
            padding: "12px 8px",
            fontSize: "0.8125rem",
          },
        },
        head: {
          fontWeight: 700,
          backgroundColor: designTokens.backgrounds.elevated,
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: designTokens.transitions.fast,
          "&:hover": {
            backgroundColor: "rgba(0, 201, 255, 0.04)",
          },
        },
      },
    },
    // Inputs mejorados
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.sm,
          transition: designTokens.transitions.normal,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.23)",
            transition: designTokens.transitions.normal,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.colors.primary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.colors.primary,
            boxShadow: `0 0 0 3px ${designTokens.borders.primary}`,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.colors.error,
          },
        },
        input: {
          color: "#FFFFFF",
          "&::placeholder": {
            color: "#B0BEC5",
            opacity: 1,
          },
        },
      },
    },
    // TextField responsive
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            "@media (max-width: 600px)": {
              fontSize: "0.9375rem",
            },
          },
        },
      },
    },
    // Autocomplete mejorado
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: designTokens.backgrounds.elevated,
          border: `1px solid ${designTokens.borders.primary}`,
          borderRadius: designTokens.radius.sm,
          boxShadow: designTokens.shadows.elevated,
        },
        option: {
          color: "#FFFFFF",
          padding: "12px 16px",
          '&[aria-selected="true"]': {
            backgroundColor: "rgba(0, 201, 255, 0.15)",
          },
          "&:hover": {
            backgroundColor: "rgba(0, 201, 255, 0.08)",
          },
        },
        listbox: {
          backgroundColor: designTokens.backgrounds.elevated,
          padding: 0,
        },
        noOptions: {
          color: "#B0BEC5",
          padding: 16,
        },
      },
    },
    // Labels mejorados
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#B0BEC5",
          "&.Mui-focused": {
            color: designTokens.colors.primary,
          },
          "&.Mui-error": {
            color: designTokens.colors.error,
          },
        },
      },
    },
    // Chips responsive
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.sm,
          fontWeight: 500,
          "@media (max-width: 600px)": {
            height: 28,
            fontSize: "0.75rem",
          },
        },
      },
    },
    // Dialog responsive
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.radius.lg,
          backgroundImage: "none",
          "@media (max-width: 600px)": {
            margin: 16,
            maxHeight: "calc(100% - 32px)",
            width: "calc(100% - 32px)",
          },
        },
      },
    },
    // DialogTitle
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 600,
          "@media (max-width: 600px)": {
            fontSize: "1.125rem",
            padding: "16px",
          },
        },
      },
    },
    // Menu mejorado
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: designTokens.backgrounds.elevated,
          border: `1px solid ${designTokens.borders.light}`,
          borderRadius: designTokens.radius.sm,
          boxShadow: designTokens.shadows.elevated,
        },
      },
    },
    // MenuItem
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: designTokens.transitions.fast,
          "&:hover": {
            backgroundColor: "rgba(0, 201, 255, 0.08)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 201, 255, 0.12)",
            "&:hover": {
              backgroundColor: "rgba(0, 201, 255, 0.16)",
            },
          },
        },
      },
    },
    // Tabs responsive
    MuiTabs: {
      styleOverrides: {
        root: {
          "@media (max-width: 600px)": {
            minHeight: 40,
          },
        },
        indicator: {
          background: designTokens.gradients.primary,
          height: 3,
          borderRadius: "3px 3px 0 0",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          transition: designTokens.transitions.fast,
          "@media (max-width: 600px)": {
            fontSize: "0.8125rem",
            minHeight: 40,
            padding: "8px 12px",
          },
        },
      },
    },
    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: designTokens.backgrounds.elevated,
          border: `1px solid ${designTokens.borders.light}`,
          borderRadius: designTokens.radius.sm,
          fontSize: "0.8125rem",
          padding: "8px 12px",
          boxShadow: designTokens.shadows.elevated,
        },
      },
    },
    // Snackbar responsive
    MuiSnackbar: {
      styleOverrides: {
        root: {
          "@media (max-width: 600px)": {
            left: 16,
            right: 16,
            bottom: 16,
          },
        },
      },
    },
    // Alert mejorado
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.sm,
        },
      },
    },
    // Fab responsive
    MuiFab: {
      styleOverrides: {
        root: {
          "@media (max-width: 600px)": {
            width: 48,
            height: 48,
          },
        },
      },
    },
    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: designTokens.transitions.fast,
          "&:hover": {
            backgroundColor: "rgba(0, 201, 255, 0.08)",
          },
        },
      },
    },
    // List items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.radius.sm,
          transition: designTokens.transitions.fast,
          "&.Mui-selected": {
            background: designTokens.gradients.primary,
            color: "#000",
            "&:hover": {
              background: designTokens.gradients.primaryHover,
            },
            "& .MuiListItemIcon-root": {
              color: "#000",
            },
          },
        },
      },
    },
    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          "&::after": {
            background: `linear-gradient(90deg, transparent, rgba(0, 201, 255, 0.1), transparent)`,
          },
        },
      },
    },
  },
});

// Aplicar responsive font sizes automáticamente
theme = responsiveFontSizes(theme, {
  breakpoints: ["sm", "md", "lg"],
  factor: 2,
});

export default theme;
